import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { filters, type BillingApi } from '@eis/billingservice-sdk';
import { useApiClients } from './ApiClientsProvider';
import { apiKeys } from './keys';
import { isSet, listQueryKey, pageOf } from './list-query';
import { isApiError, unwrap } from './result';
import { useServiceMutation, type ServiceMutation } from './useServiceMutation';

/**
 * The billing data layer (SDD-015 §7, prompt 21 phase 5) — the money the instance layer owes,
 * read and written through the billing service's generated client and nothing else (T-7).
 *
 * Three facts about this API shape every hook here:
 *
 *  - The lists speak the company list standard (SDD-017): `filters` built with the SDK's
 *    `filters()` builder from a typed filter (`policyCode==P-10442;type==estimated`, dates as
 *    `billingIntervalFrom>=2026-07-01`), `order` one catalog key with an optional `-`, `expand` a
 *    comma list, and an OPAQUE `pageToken` — never a page number. The legacy `name:value` DSL is
 *    gone, and so is the console's builder of it. The key carries the built query so two spellings
 *    of one request share a cache entry.
 *  - Money is an integer in MINOR units on the wire (STOP-2 interim); `formatCurrency` renders it.
 *    The five legacy doubles on an item (`pricePerUnit`, `quantity`, `taxRate`, `pricePerDay`,
 *    `quantityPerDays`) are display values, never summed.
 *  - Invoices and bills are insert-only and carry no ETag (B-4); the schedule row and the runs are
 *    versioned and guarded (T-12). A refusal a re-read cannot change (the period is already
 *    invoiced, the invoice is not revertible, …) comes back as an ordinary `ApiError` with its
 *    reason, never as a conflict — the SDK runtime lists the billing reasons for exactly that.
 */
const V1 = '/billingservice/v1';

export type InvoiceType =
  | 'initial'
  | 'recurring'
  | 'correction'
  | 'estimated'
  | 'final'
  | 'withdraw'
  | 'other'
  | 'penalty';
export type InvoiceStatus = 'open' | 'paid' | 'partially-paid' | 'refunded' | 'estimated';
export type InvoiceExpand = 'items' | 'statuses' | 'estimateHistory';
export type BillRunKind = 'INVOICE' | 'ESTIMATE' | 'RECONCILE';
/** The two states an operator moves a schedule row between (the others are the service's). */
export type ScheduleOperatorStatus = 'pending' | 'paused';

/** The refusal reasons the billing service answers with (SDD-015 §6.8, `pkg/svccontract`). */
export const BILLING_REASONS = {
  policyNotFound: 'POLICY_NOT_FOUND',
  periodAlreadyInvoiced: 'PERIOD_ALREADY_INVOICED',
  currencyMismatch: 'CURRENCY_MISMATCH',
  invoiceNotRevertible: 'INVOICE_NOT_REVERTIBLE',
  runInProgress: 'RUN_IN_PROGRESS',
  nothingToBill: 'NOTHING_TO_BILL',
  noSchedule: 'NO_SCHEDULE',
  billingConfigUnresolvable: 'BILLING_CONFIG_UNRESOLVABLE',
  policyReadUnavailable: 'POLICY_READ_UNAVAILABLE',
  invalidFilter: 'INVALID_FILTER',
  amountOutOfRange: 'AMOUNT_OUT_OF_RANGE',
  permissionDenied: 'PERMISSION_DENIED',
} as const;

/**
 * The billing service is unreachable (502/503/504). A screen renders that as an OUTAGE — the
 * policy still shows, the billing tab says the service did not answer — never as "no invoices"
 * (§7.2, `billing:outage`).
 */
export function isBillingUnavailable(error: unknown): boolean {
  return isApiError(error) && (error.status === 502 || error.status === 503 || error.status === 504);
}

type ListInvoicesQuery = NonNullable<
  BillingApi.operations['BillingService_ListInvoices']['parameters']['query']
>;
type ListBillRunsQuery = NonNullable<
  BillingApi.operations['BillingService_ListBillRuns']['parameters']['query']
>;

export interface InvoiceFilter {
  /** The billed entity's code — a policy's OWN invoices, or a master's (B-8). */
  readonly policyCode?: string;
  readonly groupPolicyCode?: string;
  /** Invoices whose LINES name this policy: a member's periods billed through its master. */
  readonly itemPolicyCode?: string;
  readonly type?: InvoiceType;
  readonly status?: InvoiceStatus;
  readonly invoiceNumber?: string;
  /**
   * Civil dates (`YYYY-MM-DD`): invoices whose period starts on or after `periodFrom` and ends on
   * or before `periodTo`. A month is `periodFrom` = its first day, `periodTo` = the first day of
   * the next month — periods are half-open, as the service prices them (§5.3).
   */
  readonly periodFrom?: string;
  readonly periodTo?: string;
  /** Free text over invoice number and policy code/number. */
  readonly search?: string;
  /** One catalog key, `-` prefixed for descending; the service's default is `createdAt`. */
  readonly order?: string;
  readonly expand?: readonly InvoiceExpand[];
  readonly pageSize?: number;
  /** Opaque cursor from the previous response. Never constructed by the console. */
  readonly pageToken?: string;
  /** Ask for `totalSize` (counted up to 10 000, `totalSizeCapped` beyond). */
  readonly includeTotal?: boolean;
}

/** The request an {@link InvoiceFilter} means — every narrowing a `filters` clause. */
function invoiceQuery(filter: InvoiceFilter): ListInvoicesQuery {
  const f = filters();
  if (isSet(filter.policyCode)) f.eq('policyCode', filter.policyCode);
  if (isSet(filter.groupPolicyCode)) f.eq('groupPolicyCode', filter.groupPolicyCode);
  if (isSet(filter.itemPolicyCode)) f.eq('itemPolicyCode', filter.itemPolicyCode);
  if (filter.type !== undefined) f.eq('type', filter.type);
  if (filter.status !== undefined) f.eq('status', filter.status);
  if (isSet(filter.invoiceNumber)) f.eq('invoiceNumber', filter.invoiceNumber);
  if (isSet(filter.periodFrom)) f.ge('billingIntervalFrom', filter.periodFrom);
  if (isSet(filter.periodTo)) f.le('billingIntervalTo', filter.periodTo);
  return {
    ...(f.isEmpty() ? {} : { filters: f.toString() }),
    ...(isSet(filter.search) ? { search: filter.search } : {}),
    ...(isSet(filter.order) ? { order: filter.order } : {}),
    ...(filter.expand !== undefined && filter.expand.length > 0 ? { expand: filter.expand.join(',') } : {}),
    ...pageOf(filter),
    ...(filter.includeTotal === true ? { includeTotal: true } : {}),
  };
}

/** Drops the fields a caller left undefined, so the wire carries only what was said. */
function compact<T extends object>(body: T): { [K in keyof T]?: Exclude<T[K], undefined> } {
  return Object.fromEntries(
    Object.entries(body).filter(([, value]) => value !== undefined),
  ) as { [K in keyof T]?: Exclude<T[K], undefined> };
}

// ---------------------------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------------------------

/**
 * The invoices, SERVER-side filtered and paged (§7.1): a tenant with forty thousand invoices has
 * to work the same way as one with four. Items and statuses come only when asked for (`expand`).
 */
export function useInvoices(filter: InvoiceFilter = {}): UseQueryResult<BillingApi.ListInvoicesResponse> {
  const { billing } = useApiClients();
  const query = invoiceQuery(filter);
  return useQuery({
    queryKey: apiKeys.billing.invoices(listQueryKey(query)),
    queryFn: () => unwrap(billing.client.GET(`${V1}/invoices`, { params: { query } })),
  });
}

/** One invoice with its items and statuses; `expandHistory` adds the superseded estimates (§6.7). */
export function useInvoice(
  code: string,
  expandHistory = false,
): UseQueryResult<BillingApi.GetInvoiceResponse> {
  const { billing } = useApiClients();
  return useQuery({
    queryKey: [...apiKeys.billing.invoice(code), expandHistory ? 'with-history' : 'plain'],
    queryFn: () =>
      unwrap(
        billing.client.GET(`${V1}/invoices/{code}`, {
          params: {
            path: { code },
            ...(expandHistory ? { query: { expand: 'estimateHistory' } } : {}),
          },
        }),
      ),
    enabled: code !== '',
  });
}

/**
 * A policy's OWN invoices, newest first, with their lines — a direct policy's periods, or a
 * master's collective documents (B-8). A member billed through its master has none here; its
 * periods are `useMasterInvoiceLines`.
 */
export function usePolicyInvoices(policyCode: string): UseQueryResult<BillingApi.ListInvoicesResponse> {
  const { billing } = useApiClients();
  const query = invoiceQuery({
    policyCode,
    order: '-createdAt',
    expand: ['items', 'statuses'],
    pageSize: 200,
  });
  return useQuery({
    queryKey: apiKeys.billing.policyInvoices(policyCode),
    queryFn: () => unwrap(billing.client.GET(`${V1}/invoices`, { params: { query } })),
    enabled: policyCode !== '',
  });
}

/** One line of a master's invoice, with the invoice it sits on. */
export interface MasterInvoiceLine {
  readonly invoice: BillingApi.Invoice;
  readonly item: BillingApi.InvoiceItem;
}

/**
 * The master lines naming a member (§7.2 "billed through the Gruppen-Police"): every invoice
 * billed to ANOTHER entity whose items carry this policy's code, reduced to those lines. Newest
 * invoice first, lines in the order the master carries them. The policy's own documents are
 * `usePolicyInvoices`, not master lines; the master's ESTIMATE names the member on a line too
 * (A-52) and is `useMemberEstimate`, not a master line — an estimate is not a document.
 */
export function useMasterInvoiceLines(policyCode: string): UseQueryResult<readonly MasterInvoiceLine[]> {
  const { billing } = useApiClients();
  const query = invoiceQuery({
    itemPolicyCode: policyCode,
    order: '-createdAt',
    expand: ['items'],
    pageSize: 200,
  });
  return useQuery({
    queryKey: apiKeys.billing.masterLines(policyCode),
    queryFn: () => unwrap(billing.client.GET(`${V1}/invoices`, { params: { query } })),
    select: (page): readonly MasterInvoiceLine[] =>
      (page.items ?? [])
        .filter((invoice) => invoice.policyCode !== policyCode && invoice.type !== 'estimated')
        .flatMap((invoice) =>
        (invoice.invoiceItems ?? [])
          .filter((item) => item.policyCode === policyCode)
          .map((item) => ({ invoice, item })),
      ),
    enabled: policyCode !== '',
  });
}

/**
 * The policy's schedule row (B-10): its billing mode, the next invoice date, the next period, the
 * status — or `null` when the service has no row yet (the policy was never reconciled). Two
 * requests on purpose: the list finds the row by policy, the GET by code is what parks the ETag
 * a later pause/resume quotes (T-12).
 */
export function usePolicyBilling(
  policyCode: string,
): UseQueryResult<BillingApi.GetPolicyBillingResponse | null> {
  const { billing } = useApiClients();
  return useQuery({
    queryKey: apiKeys.billing.schedule(policyCode),
    queryFn: async () => {
      const page = await unwrap(
        billing.client.GET(`${V1}/policies-billings`, {
          params: { query: { filters: filters().eq('policyCode', policyCode).toString(), pageSize: 1 } },
        }),
      );
      const code = page.items?.[0]?.code;
      if (code === undefined || code === '') return null;
      return unwrap(
        billing.client.GET(`${V1}/policies-billings/{code}`, { params: { path: { code } } }),
      );
    },
    enabled: policyCode !== '',
  });
}

/**
 * The running estimate (B-9, §6.7): the NEWEST persisted `estimated` invoice of the policy with the
 * superseded ones under `estimateHistory`, or `null` when no run has estimated it yet. A READ of
 * what the last ESTIMATE run wrote — `useRequestEstimate` is the on-demand write.
 */
export function useEstimate(policyCode: string): UseQueryResult<BillingApi.Invoice | null> {
  const { billing } = useApiClients();
  const query = invoiceQuery({
    policyCode,
    type: 'estimated',
    order: '-createdAt',
    expand: ['items', 'estimateHistory'],
    pageSize: 1,
  });
  return useQuery({
    queryKey: apiKeys.billing.estimate(policyCode),
    queryFn: () => unwrap(billing.client.GET(`${V1}/invoices`, { params: { query } })),
    select: (page): BillingApi.Invoice | null => page.items?.[0] ?? null,
    enabled: policyCode !== '',
  });
}

/** A member's share of its master's running estimate: the master's document and the member's line on it. */
export interface MemberEstimate {
  readonly estimate: BillingApi.Invoice;
  readonly line: BillingApi.InvoiceItem | null;
}

/**
 * The estimate a MEMBER is part of (A-52): a member billed through its Gruppen-Police has no
 * estimate document of its own — the master's newest `estimated` document carries one line per
 * member, and this is that document with the member's line picked out. `null` when there is none.
 */
export function useMemberEstimate(policyCode: string): UseQueryResult<MemberEstimate | null> {
  const { billing } = useApiClients();
  const query = invoiceQuery({
    itemPolicyCode: policyCode,
    type: 'estimated',
    order: '-createdAt',
    expand: ['items', 'estimateHistory'],
    pageSize: 1,
  });
  return useQuery({
    queryKey: apiKeys.billing.memberEstimate(policyCode),
    queryFn: () => unwrap(billing.client.GET(`${V1}/invoices`, { params: { query } })),
    select: (page): MemberEstimate | null => {
      const estimate = page.items?.[0];
      if (estimate === undefined) return null;
      return { estimate, line: (estimate.invoiceItems ?? []).find((item) => item.policyCode === policyCode) ?? null };
    },
    enabled: policyCode !== '',
  });
}

/** The immutable priced statement behind an invoice line (B-3), for the audit read. */
export function useBill(code: string): UseQueryResult<BillingApi.GetBillResponse> {
  const { billing } = useApiClients();
  return useQuery({
    queryKey: apiKeys.billing.bill(code),
    queryFn: () => unwrap(billing.client.GET(`${V1}/bills/{code}`, { params: { path: { code } } })),
    // A bill never changes once recorded; its digest is its identity.
    staleTime: Infinity,
    enabled: code !== '',
  });
}

export interface BillRunFilter {
  readonly kind?: BillRunKind;
  /** Civil dates: runs whose run date lies in `[from, to]`. */
  readonly from?: string;
  readonly to?: string;
  readonly pageSize?: number;
  /** Opaque cursor from the previous response. Never constructed by the console. */
  readonly pageToken?: string;
}

function billRunQuery(filter: BillRunFilter): ListBillRunsQuery {
  const f = filters();
  if (filter.kind !== undefined) f.eq('kind', filter.kind);
  if (isSet(filter.from)) f.ge('runDate', filter.from);
  if (isSet(filter.to)) f.le('runDate', filter.to);
  return { ...(f.isEmpty() ? {} : { filters: f.toString() }), ...pageOf(filter) };
}

/** The bill runs (§5.6): kind, date, counts, outcome — newest first. */
export function useBillRuns(filter: BillRunFilter = {}): UseQueryResult<BillingApi.ListBillRunsResponse> {
  const { billing } = useApiClients();
  const query = billRunQuery(filter);
  return useQuery({
    queryKey: apiKeys.billing.runs(listQueryKey(query)),
    queryFn: () => unwrap(billing.client.GET(`${V1}/bill-runs`, { params: { query } })),
  });
}

/** One run with its report. */
export function useBillRun(code: string): UseQueryResult<BillingApi.GetBillRunResponse> {
  const { billing } = useApiClients();
  return useQuery({
    queryKey: apiKeys.billing.run(code),
    queryFn: () =>
      unwrap(billing.client.GET(`${V1}/bill-runs/{code}`, { params: { path: { code } } })),
    enabled: code !== '',
  });
}

// ---------------------------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------------------------

/**
 * What an issue on a policy may have touched: every list, the schedule, and every policy's billing
 * view — because an issue on a MEMBER writes the master's invoice and every sibling's master lines
 * (B-8), and the variables cannot name those policies.
 */
function afterIssue(policyCode: string): readonly (readonly string[])[] {
  return [
    apiKeys.billing.invoiceList(),
    apiKeys.billing.policies(),
    apiKeys.billing.schedule(policyCode),
    apiKeys.billing.scheduleList(),
  ];
}

export interface IssueInvoiceVariables {
  readonly policyCode: string;
  /** `initial` for the first period, `recurring` afterwards — the service refuses the wrong one. */
  readonly kind: 'initial' | 'recurring';
  /**
   * An explicit period ("invoice for a period", §7.1); left out, the service bills the schedule's
   * next period (B-10). Civil dates, half-open.
   */
  readonly billingIntervalFrom?: string;
  readonly billingIntervalTo?: string;
  readonly dueDate?: string;
  readonly invoiceNumber?: string;
  readonly metadata?: Record<string, unknown>;
  readonly shouldSetNextBillingDate?: boolean;
}

/** What an issue answers: the document, or `noChange` when the period had nothing to bill (B-11). */
export interface IssuedInvoice {
  readonly invoice?: BillingApi.Invoice;
  readonly noChange: boolean;
}

/**
 * Issues an invoice NOW for a direct policy or a master — the legacy `initial-invoices` /
 * `recurring-invoices` paths (§6.1, §6.2). A member billed through its master is refused with
 * `NO_SCHEDULE`; an already-invoiced period with `PERIOD_ALREADY_INVOICED`.
 */
export function useIssueInvoice(): ServiceMutation<IssueInvoiceVariables, IssuedInvoice> {
  const { billing } = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ kind, ...variables }) => {
      const body = compact({
        policyCode: variables.policyCode,
        billingIntervalFrom: variables.billingIntervalFrom,
        billingIntervalTo: variables.billingIntervalTo,
        dueDate: variables.dueDate,
        invoiceNumber: variables.invoiceNumber,
        metadata: variables.metadata,
        shouldSetNextBillingDate: variables.shouldSetNextBillingDate,
      });
      if (kind === 'initial') {
        const response = await unwrap(billing.client.POST(`${V1}/initial-invoices`, { body }));
        return { invoice: response.invoice, noChange: false };
      }
      const response = await unwrap(billing.client.POST(`${V1}/recurring-invoices`, { body }));
      return { invoice: response.invoice, noChange: response.noChange === true };
    },
    invalidates: ({ policyCode }) => afterIssue(policyCode),
  });
}

export interface ScheduleInvoiceVariables {
  readonly policyCode: string;
}

/**
 * Puts a policy on the schedule (B-10): the legacy `CreatePolicyBilling`, which the service
 * answers with the row it computes — billing mode, next invoice date, next period — creating it
 * when the policy has none yet and returning the existing one otherwise. The DATE is the service's
 * (anchor, alignment, lead days from the resolved config), never typed by an operator; a specific
 * period is billed with `useIssueInvoice` and an explicit interval (DECISIONS A-28).
 */
export function useScheduleInvoice(): ServiceMutation<
  ScheduleInvoiceVariables,
  BillingApi.CreatePolicyBillingResponse
> {
  const { billing } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ policyCode }) =>
      unwrap(billing.client.POST(`${V1}/policies-billings`, { body: { policyCode } })),
    invalidates: ({ policyCode }) => [
      apiKeys.billing.schedule(policyCode),
      apiKeys.billing.scheduleList(),
    ],
  });
}

export interface UpdatePolicyBillingStatusVariables {
  readonly policyCode: string;
  /** The row's code, from `usePolicyBilling`. */
  readonly code: string;
  readonly status: ScheduleOperatorStatus;
  /** The row's version the screen is looking at (T-12); the SDK adds the matching `If-Match`. */
  readonly expectedVersion: string;
}

/** Pauses or resumes a schedule row; the service moves the version, a lost race reloads. */
export function useUpdatePolicyBillingStatus(): ServiceMutation<
  UpdatePolicyBillingStatusVariables,
  BillingApi.UpdatePolicyBillingResponse
> {
  const { billing } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, status, expectedVersion }) =>
      unwrap(
        billing.client.PUT(`${V1}/policies-billings/{code}`, {
          params: { path: { code } },
          body: { code, status, expectedVersion },
        }),
      ),
    invalidates: ({ policyCode }) => [
      apiKeys.billing.schedule(policyCode),
      apiKeys.billing.scheduleList(),
    ],
  });
}

export interface CorrectPolicyInvoicesVariables {
  readonly policyCode: string;
  /** The invoiced period to re-price (civil dates, half-open). */
  readonly billingIntervalFrom: string;
  readonly billingIntervalTo: string;
  readonly dueDate?: string;
  readonly metadata?: Record<string, unknown>;
}

/**
 * Re-prices an invoiced period from the policy's CURRENT timeline and issues the delta as a
 * `correction` (§6.3, B-11) — on the master when the policy is a member. `noChange` when the
 * amounts did not move or the delta is under the configured minimum.
 */
export function useCorrectPolicyInvoices(): ServiceMutation<
  CorrectPolicyInvoicesVariables,
  BillingApi.CreateCorrectionInvoicesResponse
> {
  const { billing } = useApiClients();
  return useServiceMutation({
    mutationFn: (variables) =>
      unwrap(
        billing.client.POST(`${V1}/correction-invoices`, {
          body: compact({
            policyCode: variables.policyCode,
            billingIntervalFrom: variables.billingIntervalFrom,
            billingIntervalTo: variables.billingIntervalTo,
            dueDate: variables.dueDate,
            metadata: variables.metadata,
          }),
        }),
      ),
    invalidates: ({ policyCode }) => afterIssue(policyCode),
  });
}

export interface RevertInvoiceVariables {
  readonly invoiceCode: string;
  /** The billed entity, for the invalidation; the service knows it from the invoice. */
  readonly policyCode: string;
}

/**
 * Reverts an invoice: a `correction` that negates every line, the original moving to `refunded`
 * (§6.3). Estimates, refunded invoices and reversals are refused with `INVOICE_NOT_REVERTIBLE`.
 */
export function useRevertInvoice(): ServiceMutation<RevertInvoiceVariables, BillingApi.RevertInvoiceResponse> {
  const { billing } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ invoiceCode }) =>
      unwrap(billing.client.POST(`${V1}/correction-invoices/revert`, { body: { invoiceCode } })),
    invalidates: ({ invoiceCode, policyCode }) => [
      apiKeys.billing.invoice(invoiceCode),
      ...afterIssue(policyCode),
    ],
  });
}

export interface ManualInvoiceItem {
  readonly name: string;
  readonly quantity: number;
  /** In MAJOR units, as the legacy dialog takes it; the service prices and rounds (B-17). */
  readonly pricePerUnit: number;
}

export interface CreateManualInvoiceVariables {
  readonly policyCode: string;
  /** Defaults to `other`; `estimated` and `penalty` are the service's own. */
  readonly type?: Exclude<InvoiceType, 'estimated' | 'penalty'>;
  readonly billingIntervalFrom: string;
  readonly billingIntervalTo: string;
  readonly dueDate?: string;
  readonly invoiceNumber?: string;
  readonly metadata?: Record<string, unknown>;
  readonly items: readonly ManualInvoiceItem[];
}

/** A manual invoice with free items — the legacy `POST /invoices` (§7.1). */
export function useCreateManualInvoice(): ServiceMutation<
  CreateManualInvoiceVariables,
  BillingApi.CreateInvoiceResponse
> {
  const { billing } = useApiClients();
  return useServiceMutation({
    mutationFn: (variables) =>
      unwrap(
        billing.client.POST(`${V1}/invoices`, {
          body: compact({
            policyCode: variables.policyCode,
            type: variables.type,
            billingIntervalFrom: variables.billingIntervalFrom,
            billingIntervalTo: variables.billingIntervalTo,
            dueDate: variables.dueDate,
            invoiceNumber: variables.invoiceNumber,
            metadata: variables.metadata,
            items: variables.items.map((item) => ({ ...item })),
          }),
        }),
      ),
    invalidates: ({ policyCode }) => afterIssue(policyCode),
  });
}

export interface RequestEstimateVariables {
  readonly policyCode: string;
  /** The business date whose running period to estimate; today when left out. */
  readonly validAt?: string;
}

/**
 * Asks for an estimate NOW (B-9): the service answers the newest one when the inputs did not move
 * (the digest is the key) and prices a new one otherwise. `useEstimate` then shows it.
 */
export function useRequestEstimate(): ServiceMutation<
  RequestEstimateVariables,
  BillingApi.CreateEstimatedInvoiceResponse
> {
  const { billing } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ policyCode, validAt }) =>
      unwrap(
        billing.client.POST(`${V1}/estimated-invoices`, { body: compact({ policyCode, validAt }) }),
      ),
    invalidates: ({ policyCode }) => [
      apiKeys.billing.estimate(policyCode),
      apiKeys.billing.invoiceList(),
    ],
  });
}

export interface RecordInvoiceStatusVariables {
  readonly invoiceCode: string;
  readonly policyCode: string;
  readonly status: Exclude<InvoiceStatus, 'estimated'>;
}

/** Appends a status by hand — the legacy `CreateInvoiceStatus` (payments themselves are out, B-13). */
export function useRecordInvoiceStatus(): ServiceMutation<
  RecordInvoiceStatusVariables,
  BillingApi.CreateInvoiceStatusResponse
> {
  const { billing } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ invoiceCode, status }) =>
      unwrap(
        billing.client.POST(`${V1}/invoices/{invoiceCode}/statuses`, {
          params: { path: { invoiceCode } },
          body: { invoiceCode, status },
        }),
      ),
    invalidates: ({ invoiceCode, policyCode }) => [
      apiKeys.billing.invoice(invoiceCode),
      ...afterIssue(policyCode),
    ],
  });
}

export interface RequestBillRunVariables {
  readonly kind: BillRunKind;
  /** The run date (civil); the run selects every row due on or before it. */
  readonly runDate: string;
}

/**
 * Starts a bill run for a date (§5.6). A run touches the whole book, so everything billing is
 * re-read afterwards — the one place a root invalidation is the honest rule.
 */
export function useRequestBillRun(): ServiceMutation<RequestBillRunVariables, BillingApi.RequestBillRunResponse> {
  const { billing } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ kind, runDate }) =>
      unwrap(billing.client.POST(`${V1}/bill-runs`, { body: { kind, runDate } })),
    invalidates: () => [apiKeys.billing.root],
  });
}
